import { Box, Grid, Link } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNextPaymentInfo, getWorkspaceCommunicationCredits } from 'wisestep-api';
import {
  getDaysBetweenDates,
  Item,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import AlingTextSideBySide from '../../../components/AlingTextSideBySide';
import { useWorkspaceSKUContext } from '../../../context';
import { Loader } from '../../auth/components/OtpPageLayout';
import PlanFeatureList from '../../paymentFlow/components/PlanFeatureList';
import { getPlanFromPlanCode, planFeatures } from '../../paymentFlow/utils/helper';
import { useTotalInvitesCall } from '../../users/helper/hooks';

import MyPlanLayout from './MyPlanLayout';

interface IPaymentInfo {
  amount: string;
  nextPayment: string;
  interval: string;
}

const MyPlan = () => {
  const [payementInfo, setPaymentInfo] = useState<IPaymentInfo | null>(null);
  const [initialized, setIntialized] = useState(false);
  const [credit, setCredit] = useState<number | null>(null);
  const workspaceSKU = useWorkspaceSKUContext();
  const isExpired =
    workspaceSKU?.expiryDate &&
    getDaysBetweenDates(new Date(), new Date(workspaceSKU?.expiryDate)) <= 0;
  const navigate = useNavigate();
  const [maxInvites] = useTotalInvitesCall();
  const currentPlanName = getPlanFromPlanCode(workspaceSKU?.workspacePlan?.planCode);
  const allFeatures = planFeatures[1]?.features ?? [];

  useEffect(() => {
    (async () => {
      try {
        const response = await getWorkspaceCommunicationCredits();
        setCredit(response.data.credit);
      } catch (e) {
        setCredit(0);
      }
      try {
        const res = await getNextPaymentInfo();
        setPaymentInfo(res.data);
        setIntialized(true);
      } catch (e) {
        setIntialized(true);
      }
    })();
  }, []);

  return (
    <MyPlanLayout>
      {initialized ? (
        <>
          <Grid container>
            <Grid item xs={6} textAlign="left">
              <WsTypography bold variant={WsTypoVariants.TITLE}>
                My Plan
              </WsTypography>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <WsButton variant="outlined" onClick={() => navigate('/plan/purchase')}>
                View Plans
              </WsButton>
            </Grid>
            <Grid item xs={5.9}>
              <Box sx={{ border: `1px solid ${WsColors.BorderGrey}`, mt: 2 }}>
                <Item>
                  <WsTypography bold variant={WsTypoVariants.TITLE}>
                    {payementInfo ? 'Upcoming Bills' : 'Plan Details'}
                  </WsTypography>
                  <Box mt={2}>
                    <AlingTextSideBySide
                      heading={true}
                      isPrimary={true}
                      leftText="Wisestep Platform"
                      rightText={
                        payementInfo
                          ? `$ ${payementInfo?.amount}/${payementInfo?.interval}`
                          : currentPlanName
                      }
                      leftTextCaption={payementInfo ? '' : `${maxInvites} users`}
                    />
                    {payementInfo && (
                      <AlingTextSideBySide
                        leftText={currentPlanName}
                        leftTextCaption={`${maxInvites} users/${payementInfo?.interval}`}
                        rightText=""
                      />
                    )}
                  </Box>
                </Item>
                <Box sx={{ border: `1px solid ${WsColors.BorderGrey}`, mt: 2 }}>
                  {payementInfo ? (
                    <Item>
                      <AlingTextSideBySide
                        leftText="Total"
                        heading={true}
                        rightText={`$ ${payementInfo?.amount}`}
                      />
                      <Grid container mt={2}>
                        <Grid item xs={8} textAlign="left">
                          <WsTypography
                            variant={WsTypoVariants.BODY2}
                          >{`Next Payment is due on ${dayjs(
                            dayjs.unix(parseInt(payementInfo.nextPayment)).format()
                          ).format('MMM DD YYYY')}`}</WsTypography>
                          <WsTypography variant={WsTypoVariants.BODY2}>
                            {`We'll send and email reminder for your payment`}
                          </WsTypography>
                        </Grid>
                        {isExpired ? (
                          <Grid item xs={4}>
                            <Box sx={{ float: 'right' }}>
                              <WsButton onClick={() => navigate('/plan/purchase')}>
                                Recharge Now
                              </WsButton>
                            </Box>
                          </Grid>
                        ) : (
                          <>
                            {(currentPlanName === 'Pro' || currentPlanName === 'Enterprise') && (
                              <Grid item xs={4}>
                                <Box sx={{ float: 'right' }}>
                                  <WsButton onClick={() => navigate('/plan/purchase?user=add')}>
                                    {isExpired ? 'Recharge Now' : 'Add Users'}
                                  </WsButton>
                                </Box>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </Item>
                  ) : (
                    <Item>
                      {workspaceSKU.expiryDate && (
                        <Grid container mt={2}>
                          <Grid item xs={8} textAlign="left">
                            <WsTypography
                              variant={WsTypoVariants.BODY2}
                            >{`Next Payment is due on ${dayjs(
                              dayjs(workspaceSKU.expiryDate).format()
                            ).format('MMM DD YYYY')}`}</WsTypography>
                            <WsTypography variant={WsTypoVariants.BODY2}>
                              {`We'll send and email reminder for your payment`}
                            </WsTypography>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ float: 'right' }}>
                              <WsButton onClick={() => navigate('/plan/purchase')}>
                                {isExpired ? 'Recharge Now' : 'Manage Plan'}
                              </WsButton>
                            </Box>
                          </Grid>
                        </Grid>
                      )}
                    </Item>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={0.2}></Grid>
            <Grid item xs={5.9} mt={5} sx={{ border: `1px solid ${WsColors.BorderGrey}`, mt: 2 }}>
              <Item sx={{ pb: 0 }}>
                <WsTypography bold variant={WsTypoVariants.TITLE}>
                  Active Recharges
                </WsTypography>
              </Item>
              <Box sx={{ height: '250px' }}>
                <Item sx={{ pt: 0 }}>
                  <AlingTextSideBySide
                    isPrimary={true}
                    leftText="Features"
                    heading={true}
                    rightText="Balance"
                  />
                  <AlingTextSideBySide
                    leftText="SMS Credits"
                    rightText={credit != null ? String(credit) : 'Loading...'}
                  />
                </Item>
              </Box>
            </Grid>
          </Grid>
          <Grid container sx={{ border: `1px solid ${WsColors.BorderGrey}`, mt: 2 }}>
            <Grid item xs={12}>
              <Item>
                <Box sx={{ float: 'left' }}>
                  <WsTypography bold variant={WsTypoVariants.TITLE}>
                    Wisestep
                  </WsTypography>
                  <WsTypography bold color={WsTypoColors.PRIMARY}>
                    {`Current Plan - ${currentPlanName}`}
                  </WsTypography>
                </Box>
              </Item>
            </Grid>
            <Item>
              <PlanFeatureList features={allFeatures} />
            </Item>
          </Grid>
          <Item>
            <Box textAlign="center">
              <WsTypography bold color={WsTypoColors.SECONDARY}>
                Do you need some help to know the right plan that fits your needs ? Please let our
                support team answer you
              </WsTypography>
              <WsTypography bold color={WsTypoColors.SECONDARY} display="inline">
                Contact us at
              </WsTypography>
              <Link ml={0.6} href="mailto:sales@wisestep-inc.com">
                sales@wisestep-inc.com
              </Link>
            </Box>
          </Item>
        </>
      ) : (
        <Loader />
      )}
    </MyPlanLayout>
  );
};

export default MyPlan;
